// ============================================================
// TUTORIAL CONFIGURATION
// Generated by Learn by Building Tutorial Generator
// Date: 2026-02-10
// Source: Video recording - "Analyzing Video Responses"
// ============================================================
//
// NOTE: This tutorial demonstrates the CHALLENGE-BASED writing style:
// - title: What to accomplish (not "click X")
// - instruction: Context and goal (not specific UI directions)
// - hint: Specific location guidance (shown only when user requests)
//
// The intro screen will display:
// - config.title as the main heading
// - config.description as the explanation
// - config.estimatedTime and steps count as stats
// ============================================================

// ------------------------------------------------------------
// TUTORIAL METADATA
// ------------------------------------------------------------
const tutorialConfig = {
  // Unique identifier - must match filename and screenshot folder
  id: 'analyzing-video-responses',

  // Title shown on intro screen and header during tutorial
  title: 'Analyzing Video Responses',

  // Description shown on intro screen - explain what they'll learn
  description: 'Practice how to navigate to the Audio & Video library in Data & Analysis, review individual video responses with transcripts and sentiment tags, share a response via a shareable link, and use the AI summary to explore key themes across all responses.',

  // Time estimate shown on intro screen
  estimatedTime: '4 minutes',

  // Difficulty badge
  difficulty: 'Beginner',

  // For future use
  prerequisites: [],
  tags: ['video-responses', 'audio-video', 'data-analysis', 'ai-summary', 'sentiment', 'transcript', 'share', 'video-feedback'],

  // UI accent color (buttons, progress bar, badges)
  brandColor: '#0066CC',
};

// ------------------------------------------------------------
// TUTORIAL STEPS
// ------------------------------------------------------------
const steps = [
  {
    id: 1,
    title: "Navigate to your response data",
    instruction: "You're in the Survey Builder for a video feedback survey. Let's head to where we can analyze the collected responses.",
    screenshot: '/screenshots/analyzing-video-responses/step-1.png',
    hotspot: {
      top: 5.5,
      left: 12.6,
      width: 9,
      height: 4,
    },
    hint: 'Look for the "Data & Analysis" tab in the top navigation bar, between "Workflows" and "Results".',
    wrongClickMessage: 'Not quite! Look for the "Data & Analysis" tab in the navigation bar at the top of the page.',
    requiresInput: false,
  },
  {
    id: 2,
    title: "Let's review the video and audio content",
    instruction: "The Data Table shows your recorded responses. Let's go to the dedicated area for reviewing video and audio content.",
    screenshot: '/screenshots/analyzing-video-responses/step-2.png',
    hotspot: {
      top: 9.6,
      left: 35.8,
      width: 10,
      height: 4,
    },
    hint: 'Find the "Audio & Video" sub-tab in the secondary navigation row, to the right of "Weighting".',
    wrongClickMessage: 'That\'s not it. Look at the secondary navigation tabs below "Data & Analysis" — find "Audio & Video" on the right side.',
    requiresInput: false,
  },
  {
    id: 3,
    title: "Let's view this specific response",
    instruction: "The Audio and Video library shows all collected video responses. Let's pick one to examine in detail.",
    screenshot: '/screenshots/analyzing-video-responses/step-3.png',
    hotspot: {
      top: 93.9,
      left: 82.3,
      width: 18,
      height: 6,
    },
    hint: 'Scroll down in the right panel and click the blue "View" button at the bottom.',
    wrongClickMessage: 'Look for the blue "View" button at the bottom of the Selected response panel on the right.',
    requiresInput: false,
  },
  {
    id: 4,
    title: "We need to share this response with the team",
    instruction: "You can see the video alongside its auto-generated transcript with sentiment analysis. Let's share this response with a colleague.",
    screenshot: '/screenshots/analyzing-video-responses/step-4.png',
    hotspot: {
      top: 14,
      left: 74,
      width: 7,
      height: 4,
    },
    hint: 'Look for the "Share" button with a share icon near the top-right of the video viewer.',
    wrongClickMessage: 'Not quite! Find the "Share" button in the top-right area of the screen, above the transcript.',
    requiresInput: false,
  },
  {
    id: 5,
    title: "Set the link duration to 30 days",
    instruction: "You can create a temporary shareable link for this video. Let's adjust how long the link will remain active.",
    screenshot: '/screenshots/analyzing-video-responses/step-5.png',
    hotspot: {
      top: 52,
      left: 37,
      width: 20,
      height: 5,
    },
    hint: 'Click the duration dropdown (currently showing "48 hours") to change the link expiration period to 30 days',
    wrongClickMessage: 'Look for the duration dropdown selector in the "Create shareable link" dialog.',
    requiresInput: false,
  },
  {
    id: 6,
    title: "Generate the shareable link",
    instruction: "With the duration set to 30 days, let's create the link so it can be shared with others.",
    screenshot: '/screenshots/analyzing-video-responses/step-7.png',
    hotspot: {
      top: 51.5,
      left: 56.6,
      width: 8,
      height: 5,
    },
    hint: 'Click the blue "Create link" button next to the duration dropdown.',
    wrongClickMessage: 'Find the blue "Create link" button to the right of the duration selector.',
    requiresInput: false,
  },
  {
    id: 7,
    title: "Copy the link to your clipboard",
    instruction: "The shareable link has been generated. Let's copy it so you can paste it in an email or message.",
    screenshot: '/screenshots/analyzing-video-responses/step-8.png',
    hotspot: {
      top: 57.8,
      left: 37.3,
      width: 6,
      height: 4.5,
    },
    hint: 'Click the "Copy link" button below the generated URL.',
    wrongClickMessage: 'Look for the "Copy link" button underneath the shareable URL.',
    requiresInput: false,
  },
  {
    id: 8,
    title: "Once copied you can share it! Let's close this out",
    instruction: "We need to close this window to share the link",
    screenshot: '/screenshots/analyzing-video-responses/step-9.png',
    hotspot: {
      top: 65,
      left: 57.5,
      width: 7,
      height: 4.5,
    },
    hint: 'Click the "Close" button below the copy link option',
    wrongClickMessage: 'Look for the "Close" button underneath the copy link button.',
    requiresInput: false,
  },
  {
    id: 9,
    title: "Let's go back to check all the captured responses again",
    instruction: "We want to check the key themes of captured responses",
    screenshot: '/screenshots/analyzing-video-responses/step-4.png',
    hotspot: {
      top: 14.2,
      left: 4.6,
      width: 9,
      height: 4.5,
    },
    hint: 'Click the "All responses" button in the top-left of the video viewer.',
    wrongClickMessage: 'Look for the "All responses" button on the top-left of the video viewer.',
    requiresInput: false,
  },
  {
    id: 10,
    title: "Access the AI-powered summary",
    instruction: "Back in the library, let's use AI to get a high-level summary of themes across all video responses.",
    screenshot: '/screenshots/analyzing-video-responses/step-10.png',
    hotspot: {
      top: 14.5,
      left: 91.8,
      width: 9.5,
      height: 4.5,
    },
    hint: 'Look for the "AI summary" button in the top-right corner of the page.',
    wrongClickMessage: 'Not quite! Find the "AI summary" button near the top-right of the Audio & Video library page.',
    requiresInput: false,
  },
  {
    id: 11,
    title: "View a previously generated summary",
    instruction: "The AI summary panel shows previously generated summaries. Let's view the most recent one to explore the key themes.",
    screenshot: '/screenshots/analyzing-video-responses/step-11.png',
    hotspot: {
      top: 60,
      left: 82,
      width: 17,
      height: 5,
    },
    hint: 'Click the blue "View summary" button in the AI summary panel on the right.',
    wrongClickMessage: 'Look for the blue "View summary" button in the AI summary side panel.',
    requiresInput: false,
  },
  {
    id: 12,
    title: "After checking out the key findings and recommendations, click the navigation bar to scroll down and see the top themes",
    instruction: "The AI summary displays top themes identified across your video responses. Let's check them out",
    screenshot: '/screenshots/analyzing-video-responses/step-12.png',
    hotspot: {
      top: 24,
      left: 88,
      width: 3,
      height: 40.5,
    },
    hint: 'Click the navigation bar on the right side of the screen.',
    wrongClickMessage: 'Look for the navigation bar on the right side of the screen.',
    requiresInput: false,
  },
  {
    id: 13,
    title: "See the Comfort and Fit theme",
    instruction: "The AI summary displays top themes identified across your video responses. Let's see what users say about Comfort and Fit",
    screenshot: '/screenshots/analyzing-video-responses/step-13.png',
    hotspot: {
      top: 42.1,
      left: 33.7,
      width: 53,
      height: 10,
    },
    hint: 'Click the "Comfort and Fit" option below the top themes title',
    wrongClickMessage: 'Look for the "Comfort and Fit" option below the top themes title',
    requiresInput: false,
  },
  {
    id: 14,
    title: "Let's play the video of the first comment related to Comfort and Fit",
    instruction: "Let's better understand why this comment is related to this theme",
    screenshot: '/screenshots/analyzing-video-responses/step-14.png',
    hotspot: {
      top: 70.9,
      left: 36.7,
      width: 12,
      height: 4,
    },
    hint: 'Click the "Play response" button below the first comment on the left side',
    wrongClickMessage: 'Look for the "Play response" button below the first comment on the left side',
    requiresInput: false,
  },
];

// ------------------------------------------------------------
// SCREENSHOT CHECKLIST
// ------------------------------------------------------------
/*
All screenshots must have the SAME aspect ratio (3418×1898 source, ~1.8:1).
Save to: public/screenshots/analyzing-video-responses/

□ step-1.png:  Survey Builder view with "Data & Analysis" tab visible in top navigation
□ step-2.png:  Data & Analysis > Data Table with "Audio & Video" sub-tab visible
□ step-3.png:  Audio & Video library loaded with video response rows and AI summary panel
□ step-4.png:  Video response selected with "Selected response" side panel showing tags and "View" button
□ step-5.png:  Full video viewer with transcript, sentiment highlights, and "Share" button
□ step-6.png:  "Create shareable link" modal with 48 hours duration dropdown
□ step-7.png:  "Create shareable link" modal with 30 days selected, "Create link" button visible
□ step-8.png:  Shareable link generated with URL displayed and "Copy link" button
□ step-9.png:  Audio & Video library (full width, AI summary panel closed) with "AI summary" button in top-right
□ step-10.png: AI summary side panel open with "View summary" button and generated summary dropdown
□ step-11.png: AI summary full view showing Top Themes (collapsed) with "Expand all" button
*/

// ------------------------------------------------------------
// EXPORTS
// ------------------------------------------------------------
export { tutorialConfig, steps };
